import {
  ConferenceAutoRecordingDisabledByYouEvent,
  ConferenceAutoRecordingEnabledByYouEvent,
  ConferenceGuestAccessDisabledByYouEvent,
  ConferenceGuestAccessEnabledByYouEvent,
  IMForwarding,
  PServed,
  EditMessagePayload,
  MessageStatusType,
  ConferenceType,
  CreateConferencePayload,
  ExtendedParamsChatMessageType,
  isBotPServed,
  useIM,
  ConferenceCreatedByYouEvent,
  HistoryMessageItem,
  MessageOutgoingEvent,
  MessageIncomingEvent,
  ConferenceMemberRole,
} from '@wui/im'
import dayjs from 'dayjs'
import { IcsEvent, IcsCalendar } from 'ts-ics'
import { useRouter } from 'vue-router'

import { useAutoMarkAsReadIfExecutor } from '@/entities/chat-handlers'
import { useStoreContacts } from '@/entities/contact'
import {
  getForwardingAuthorName,
  resolveForwardingAuthorName,
  usePinEventsStore,
  chatModel,
} from '@/entities/messages'
import { fetchMessage } from '@/entities/not-in-history-messages'
import { useStoreLastReadEventIds, useStoreRooms } from '@/entities/room'
import { getCancelCalendar, getDefaultCalendar, toScheduleOrganizerIdentity } from '@/entities/schedule'
import { closeVoicePlaybackIfCurrentEvent } from '@/entities/voice-message'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useUnsentMessageHandler } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { usePromptServer } from '@/shared/composables/web-dav'
import { emitter } from '@/shared/services/emitter'
import { parseMention } from '@/shared/services/mention-helper'
import { Contact } from '@/shared/types/api'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { ChatFile } from '@/shared/types/files'
import { AddOrRemoveRoomMembers, Room } from '@/shared/types/store'
import { PSTNGUEST } from '@/shared/utils/constants'
import { chatFileToImFile, enrichChatFilesWithMediaDimensions } from '@/shared/utils/files'
import { createPrivateRoom } from '@/shared/utils/im'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { htmlToMessagePayload } from '@/shared/utils/text-entities'
import { fromPServedUser, isNotPServed, numberToUri } from '@/shared/utils/uri'

interface UseRoomResult {
  send: (t: string, room: Room, chatFiles?: ChatFile[], quotedEventId?: number, forwardingEvent?: IMForwarding, quotedText?: string, subType?: ExtendedParamsChatMessageType['subType']) => Promise<HistoryMessageItem<MessageOutgoingEvent> | undefined>
  edit: (payload: EditMessagePayload) => Promise<boolean>
  pin: (targetEventId: HistoryMessageItem['eventId'], pServed: PServed) => Promise<boolean>
  deletePin: (targetEventId: HistoryMessageItem['eventId'], pServed: PServed) => Promise<boolean>
  delete: (eventId: HistoryMessageItem['eventId'], pServed: PServed) => Promise<boolean>
  deleteForSelf: (eventId: HistoryMessageItem['eventId'], pServed: PServed) => Promise<boolean>
  sendForwardMessage: (chatEvent: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>, toPServedUser: string, quotingForwardMessage: string, chatFiles?: ChatFile[]) => Promise<HistoryMessageItem<MessageOutgoingEvent> | void>
  renameRoom: (room: Room, targetName: string) => Promise<boolean>
  setRoomIdentity: (pServed: string, identity: string) => Promise<boolean>
  changeSubscriberRole: (pServed: string, targetNumber: string, targetRole: Exclude<ConferenceMemberRole, 'Owner' | 'External'>) => Promise<boolean>
  changeAvatar: (image: File, roomId: Room['id'], pServedUser: Room['pServedUser'], executor: string, filePath?: string | undefined) => Promise<boolean>
  removeAvatar: (path: string, pServed: string, roomId: string) => Promise<void>
  createConferenceFromDialog: (chatName: string, replaceableCallId: string, userList: Array<string>) => Promise<boolean>
  addMember: (pServedUser: string, targetInternalNumber: string, command?: 'AddMember' | 'AddMemberAndInvite', temporary?: boolean, role?: ConferenceMemberRole) => Promise<boolean>
  removeMember: (room: Room, targetInternalNumber: Contact['internalNumber']) => Promise<void>
  leaveRoom: (room: Room) => Promise<boolean>
  setupPincode: (pServed: string) => Promise<void>
  disablePincode: (pServed: string) => Promise<void>
  changePincode: (pServed: string) => Promise<void>
  deleteRoom: (room: Room) => Promise<boolean>
  enableSchedule: (pServed: Room['pServedUser'], event: IcsEvent) => Promise<boolean>
  disableSchedule: (pServed: Room['pServedUser'], eventUID: IcsCalendar) => Promise<boolean>
  updateSchedule: (pServed: Room['pServedUser'], event: IcsEvent) => Promise<boolean>
  createConference: (params: CreateConferencePayload) => Promise<ConferenceCreatedByYouEvent>
  disableGuestAccess: (pServed: PServed) => Promise<HistoryMessageItem<ConferenceGuestAccessDisabledByYouEvent>>
  enableGuestAccess: (pServed: PServed) => Promise<HistoryMessageItem<ConferenceGuestAccessEnabledByYouEvent>>
  enableAutoRecording: (pServed: string) => Promise<HistoryMessageItem<ConferenceAutoRecordingEnabledByYouEvent>>
  disableAutoRecording: (pServed: string) => Promise<HistoryMessageItem<ConferenceAutoRecordingDisabledByYouEvent>>
  refreshAccessToken: (PServed: string) => Promise<void>
  resend: (event: HistoryMessageItem<MessageOutgoingEvent>, roomPServed: string) => Promise<HistoryMessageItem<MessageOutgoingEvent> | undefined>
}

function createOutgoingEvent (text: string, to: PServed, extendedParams: ExtendedParamsChatMessageType = { era: 0, sender: '' }): HistoryMessageItem<MessageOutgoingEvent> {
  const eventId = dayjs().tz().valueOf()

  const event: HistoryMessageItem<MessageOutgoingEvent> = {
    eventId,
    timestamp: eventId / 1000,
    rank: eventId / 1000,
    type: 0,
    status: MessageStatusType.INITIAL,
    item: {
      type: 'OutgoingMessageEvent',
      pServed: to,
      messageText: text,
      executor: extendedParams.sender!,
      ...extendedParams,
    },
  }

  return event
}

export function useRoom (): UseRoomResult {
  const router = useRouter()
  const im = useIM()
  const storeRooms = useStoreRooms()
  const { addMessage, updateMessageStatus, updateMessage } = chatModel.useChatMessageStore()
  const { getPinEventsForRoom, setPinnedMessages } = usePinEventsStore()
  const { currentUser } = useCurrentUser()
  const { setUnsentMessage, deleteUnsentMessage } = useUnsentMessageHandler()
  const { t } = useLocalization()
  const autoMarkAsReadIfExecutor = useAutoMarkAsReadIfExecutor()

  const enableAutoRecording = async (pServed: string): Promise<HistoryMessageItem<ConferenceAutoRecordingEnabledByYouEvent>> => {
    return im.enableAutoRecording(pServed).then((message: HistoryMessageItem<ConferenceAutoRecordingEnabledByYouEvent>) => {
      addMessage(pServed, message)
      autoMarkAsReadIfExecutor(message, pServed)
      return message
    })
  }
  const disableAutoRecording = async (pServed: string): Promise<HistoryMessageItem<ConferenceAutoRecordingDisabledByYouEvent>> => {
    return im.disableAutoRecording(pServed).then((message: HistoryMessageItem<ConferenceAutoRecordingDisabledByYouEvent>) => {
      addMessage(pServed, message)
      autoMarkAsReadIfExecutor(message, pServed)
      return message
    })
  }

  const disableGuestAccess = async (pServed: PServed): Promise<HistoryMessageItem<ConferenceGuestAccessDisabledByYouEvent>> => {
    return im.disableChatGuestAccess({ pServed }).then((message: HistoryMessageItem<ConferenceGuestAccessDisabledByYouEvent>) => {
      addMessage(pServed, message)
      autoMarkAsReadIfExecutor(message, pServed)
      return message
    })
  }
  const enableGuestAccess = async (pServed: PServed): Promise<HistoryMessageItem<ConferenceGuestAccessEnabledByYouEvent>> => {
    return im.enableChatGuestAccess({ pServed }).then((message: HistoryMessageItem<ConferenceGuestAccessEnabledByYouEvent>) => {
      addMessage(pServed, message)
      autoMarkAsReadIfExecutor(message, pServed)
      return message
    })
  }

  const refreshAccessToken = async (pServed: PServed) => {
    try {
      await im.disableChatGuestAccess({ pServed })
      await im.enableChatGuestAccess({ pServed })
    } catch (e) {
      console.error(e)
    }
  }

  const removeMember = async (room: Room, targetInternalNumber: Contact['internalNumber']) => {
    const commandResponse = await im.removeSubscriber({
      pServed: room.pServedUser,
      targetInternalNumber,
    })

    const removeMemberEventPayload: AddOrRemoveRoomMembers = {
      roomId: room.id,
      members: [targetInternalNumber],
    }
    storeRooms.removeRoomMembers(removeMemberEventPayload)
    addMessage(room.pServedUser, commandResponse)
    autoMarkAsReadIfExecutor(commandResponse, room.pServedUser)
  }

  const leaveRoom = async (room: Room) => {
    if (!currentUser.value) {
      throw new Error('currentUser is ' + currentUser.value)
    }
    const { internalNumber: currentUserInternalNumber } = currentUser.value
    await removeMember(room, currentUserInternalNumber)
    await router.push({ name: 'Main' })
    storeRooms.removeRoom(room.id)
    return true
  }

  const deleteRoom = async (room: Room) => {
    await im.removeConference({
      pServed: room.pServedUser,
    })
    await router.push({ name: 'Main' })
    storeRooms.removeRoom(room.id)
    return true
  }

  const resend = async (event: HistoryMessageItem<MessageOutgoingEvent>, roomPServed: string): Promise<HistoryMessageItem<MessageOutgoingEvent> | undefined> => {
    const { files, quotedEventId, quotedText, mentionedUsers, entities, subType } = event.item as ExtendedParamsChatMessageType
    const clientEventId = event.eventId
    const markup = entities?.length
      ? { messageText: event.item.messageText, entities }
      : htmlToMessagePayload(event.item.messageText)

    const extendedParams: ExtendedParamsChatMessageType = {  }
    if (files) {
      extendedParams.files = files
    }
    if (quotedEventId) {
      extendedParams.quotedEventId = quotedEventId
    }
    if (quotedText) {
      extendedParams.quotedText = sanitizeToText(quotedText).trim()
    }
    if (mentionedUsers) {
      extendedParams.mentionedUsers = mentionedUsers
    }
    if (markup.entities?.length) {
      extendedParams.entities = markup.entities
    }
    if (subType) {
      extendedParams.subType = subType
    }

    return new Promise((resolve, reject) => {
      im.sendMessage({
        messageText: markup.messageText,
        to: event.item.pServed,
        extendedParams,
      })
        .then((response) => {
          im.markAsRead({
            eventIdMarkedAsRead: response.eventId,
            to: event.item.pServed,
          })
          updateMessage(roomPServed, response, clientEventId)
          deleteUnsentMessage(clientEventId)

          if (currentUser.value?.imLogin) {
            useStoreLastReadEventIds().updateLastReadEventId(
              roomPServed,
              currentUser.value.imLogin,
              response.eventId,
              response.rank,
              response.timestamp,
            )
            updateMessageStatus(
              response.eventId,
              roomPServed,
              currentUser.value.imLogin,
              MessageStatusType.READ,
              response.rank,
            )
          }

          emitter.emit(EmitterEvents.USAGE_MESSAGE_SENT, {
            chars: event.item.messageText?.length ?? 0,
          })

          resolve(response)
        })
        .catch((error: unknown) => {
          if (currentUser.value) {
            updateMessageStatus(
              clientEventId,
              roomPServed,
              currentUser.value.imLogin,
              MessageStatusType.ERROR,
              event.rank,
            )
          }

          console.error(error)

          reject(new Error(t('common.failedToSendMessage')))
        })
    })
  }

  return {
    disableAutoRecording,
    enableAutoRecording,
    disableGuestAccess,
    enableGuestAccess,
    async send (text: string, room: Room, chatFiles?: ChatFile[], quotedEventId?: number,  forwardingEvent?: IMForwarding, quotedText?: string, subType?: ExtendedParamsChatMessageType['subType']): Promise<HistoryMessageItem<MessageOutgoingEvent> | undefined> {
      const markup = htmlToMessagePayload(text)
      const extendedParams: ExtendedParamsChatMessageType = {  }
      if (chatFiles?.length) {
        await enrichChatFilesWithMediaDimensions(chatFiles)
        extendedParams.files = chatFiles.map(chatFileToImFile)
      }

      if (quotedEventId) {
        extendedParams.quotedEventId = quotedEventId
      }

      if (forwardingEvent) {
        Object.assign(extendedParams, forwardingEvent)
      }

      const mentioned = parseMention(text, room?.subscribers)
      if (mentioned.length) {
        extendedParams.mentionedUsers = mentioned
      }

      if (quotedText) {
        extendedParams.quotedText = sanitizeToText(quotedText).trim()
      }

      if (markup.entities?.length) {
        extendedParams.entities = markup.entities
      }

      if (subType) {
        extendedParams.subType = subType
      }

      const event = createOutgoingEvent(markup.messageText, room.pServedUser, extendedParams)
      addMessage(room?.pServedUser, event)

      return new Promise((resolve, reject) => {
        im.sendMessage({
          messageText: event.item.messageText,
          to: event.item.pServed,
          extendedParams,
        })
          .then((response) => {
            im.markAsRead({
              eventIdMarkedAsRead: response.eventId,
              to: event.item.pServed,
            })
            updateMessage(room.pServedUser, response, event.eventId, event.rank)

            if (currentUser.value?.imLogin) {
              useStoreLastReadEventIds().updateLastReadEventId(
                room.pServedUser,
                currentUser.value.imLogin,
                response.eventId,
                response.rank,
                response.timestamp,
              )
              updateMessageStatus(
                response.eventId,
                room.pServedUser,
                currentUser.value.imLogin,
                MessageStatusType.READ,
                response.rank,
              )
            }

            emitter.emit(EmitterEvents.USAGE_MESSAGE_SENT, {
              chars: event.item.messageText?.length ?? 0,
            })

            resolve(response)
          })
          .catch((error: unknown) => {
            if (currentUser.value) {
              updateMessageStatus(
                event.eventId,
                room.pServedUser,
                currentUser.value.imLogin,
                MessageStatusType.ERROR,
                event.rank,
              )
            }

            const eventWithErrorStatus: HistoryMessageItem<MessageOutgoingEvent> = {
              ...event,
              status: MessageStatusType.ERROR,
            }
            setUnsentMessage(eventWithErrorStatus, room.pServedUser)

            console.error(error)

            reject(new Error(t('common.failedToSendMessage')))
          })
      })
    },
    async edit (payload: EditMessagePayload): Promise<boolean> {
      const markup = htmlToMessagePayload(payload.text)
      const editPayload: EditMessagePayload = {
        ...payload,
        text: markup.messageText,
      }
      if (markup.entities?.length) {
        editPayload.entities = markup.entities
      } else {
        delete editPayload.entities
      }
      const response = await im.editMessage(editPayload)
      if (response) {
        chatModel.useChatMessageStore().updateMessageText(response)
      }
      return true
    },
    async pin (targetEventId: HistoryMessageItem['eventId'], pServed: PServed): Promise<boolean> {
      return im.pinMessage({
        targetEventId,
        to: pServed,
      }).then((response) => {
        addMessage(pServed, response)
        const targetMessage = chatModel.useChatMessageStore().findById<MessageIncomingEvent | MessageOutgoingEvent>(targetEventId)
        if (targetMessage) {
          const currentPinnedMessages = getPinEventsForRoom(pServed) ?? []
          const hasPinnedMessage = currentPinnedMessages.some((message) => message.eventId === targetEventId)
          if (!hasPinnedMessage) {
            const forwarding = targetMessage.item.forwarding
            // Тело пересланного, не приписка к пересылке
            const messageText = forwarding?.forwardingText
              || targetMessage.item.messageText
              || ''
            const files = targetMessage.item.files?.length
              ? targetMessage.item.files
              : (forwarding?.forwardingFiles ?? [])
            const sender = targetMessage.item.executor ?? targetMessage.item.pServed
            setPinnedMessages(pServed, [
              ...currentPinnedMessages,
              {
                eventId: targetEventId,
                pServedUserExecuter: sender,
                rank: targetMessage.rank ?? 0,
                timestamp: targetMessage.timestamp ?? 0,
                message: messageText,
                extendedParams: {
                  files,
                  sender,
                  ...(forwarding?.forwardingEventId
                    ? {
                      forwardingEventId: forwarding.forwardingEventId,
                      forwardingText: forwarding.forwardingText || messageText,
                      forwardingChatPServed: forwarding.forwardingChatPServed,
                      forwardingAuthorName: forwarding.forwardingAuthorName,
                      forwardingFiles: forwarding.forwardingFiles?.length
                        ? forwarding.forwardingFiles
                        : files,
                      forwardingTimestamp: forwarding.forwardingTimestamp
                        ?? targetMessage.timestamp,
                    }
                    : {}),
                },
              },
            ])
          }
        }
        autoMarkAsReadIfExecutor(response, pServed)
        return true
      })
    },
    async deletePin (targetEventId: HistoryMessageItem['eventId'], pServed: PServed): Promise<boolean> {
      return im.unpinMessage({
        targetEventId,
        to: pServed,
      }).then((response) => {
        const currentPinnedMessages = getPinEventsForRoom(pServed) ?? []
        setPinnedMessages(
          pServed,
          currentPinnedMessages.filter((message) => message.eventId !== targetEventId),
        )
        autoMarkAsReadIfExecutor(response, pServed)
        return true
      })
    },

    async sendForwardMessage (forwardedMessage: HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>, toPServedUser: PServed, quotingForwardMessage: string, chatFiles?: ChatFile[]) {
      const storeContacts = useStoreContacts()

      const { eventId, item } = forwardedMessage
      const { forwarding, messageText } = item

      const forwardingAuthorName = getForwardingAuthorName(forwardedMessage)

      const extendedParamsForwardEvent: IMForwarding = {}
      if (!messageText && forwarding?.forwardingEventId) {
        if (!forwarding.forwardingAuthorName?.trim() && forwarding.forwardingChatPServed) {
          await fetchMessage(forwarding.forwardingEventId, forwarding.forwardingChatPServed)
        }
        extendedParamsForwardEvent.forwardingEventId = forwarding.forwardingEventId
        extendedParamsForwardEvent.forwardingChatPServed = forwarding.forwardingChatPServed!
        extendedParamsForwardEvent.forwardingText = forwarding.forwardingText ? forwarding?.forwardingText : messageText
        extendedParamsForwardEvent.forwardingAuthorName = resolveForwardingAuthorName(forwarding)
        extendedParamsForwardEvent.forwardingFiles = forwarding.forwardingFiles!
        extendedParamsForwardEvent.forwardingTimestamp = forwarding.forwardingTimestamp!
      } else {
        extendedParamsForwardEvent.forwardingEventId = eventId
        extendedParamsForwardEvent.forwardingChatPServed = item.pServed
        extendedParamsForwardEvent.forwardingText = messageText
        extendedParamsForwardEvent.forwardingAuthorName = forwardingAuthorName
        extendedParamsForwardEvent.forwardingFiles = item.files!
        extendedParamsForwardEvent.forwardingTimestamp = forwardedMessage.timestamp
      }

      let room: Room = storeRooms.findByPServed(toPServedUser) as Room

      if (!room) {
        const contact = storeContacts.findByPServed(toPServedUser)
        if (contact) {
          room = createPrivateRoom(toPServedUser, contact)
        }
      }

      try {
        await this.send(quotingForwardMessage, room, chatFiles, undefined, extendedParamsForwardEvent)
      } catch (e) {
        console.error(e)
      }
    },

    async renameRoom (room: Room, targetName: string) {
      const commandResponse = await im.renameConference({
        pServed: room.pServedUser,
        roomName: targetName,
      })

      storeRooms.updateRoom(room.id, { name: targetName })
      addMessage(room.pServedUser, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, room.pServedUser)

      return true
    },

    async setRoomIdentity (pServed: string, identity: string) {
      try {
        const message = await im.setConferencePublicIdentity({
          pServed,
          identity,
        })

        if (!message) {
          return false
        }

        addMessage(pServed, message)
        autoMarkAsReadIfExecutor(message, pServed)

        return true
      } catch (e) {
        console.error(e)
        return false
      }
    },

    async changeSubscriberRole (pServed: string, targetNumber: string, targetRole: Exclude<ConferenceMemberRole, 'Owner' | 'External'>) {
      try {
        const response = await im.changeSubscriberRole({
          pServed,
          targetNumber,
          targetRole,
        })
        addMessage(pServed, response)
        autoMarkAsReadIfExecutor(response, pServed)

        return true
      } catch (e) {
        console.error(e)

        return false
      }
    },

    async enableSchedule (pServed: string, event: IcsEvent) {
      type ScheduleParam = Parameters<typeof im.addConferenceSchedule>[0]['schedule']
      const identity = toScheduleOrganizerIdentity(currentUser.value)
      const commandResponse = await im.addConferenceSchedule({
        pServed,
        schedule: getDefaultCalendar(event, {
          // SEQUENCE в ICS на wire ломается бэкендом (склеивается с ORGANIZER) — revision только в клиенте
          includeSequence: false,
          ...(identity ? { identity } : {}),
        }) as ScheduleParam,
      })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
      return true
    },

    async updateSchedule (pServed: string, event: IcsEvent) {
      type ScheduleParam = Parameters<typeof im.updateConferenceSchedule>[0]['schedule']
      const identity = toScheduleOrganizerIdentity(currentUser.value)
      const commandResponse = await im.updateConferenceSchedule({
        pServed,
        schedule: getDefaultCalendar(event, {
          includeSequence: false,
          ...(identity ? { identity } : {}),
        }) as ScheduleParam,
      })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
      return true
    },

    async disableSchedule (pServed: string, roomSchedule: IcsCalendar) {
      type RoomScheduleParam = Parameters<typeof im.removeConferenceSchedule>[0]['roomSchedule']
      const commandResponse = await im.removeConferenceSchedule({
        pServed,
        roomSchedule: getCancelCalendar(roomSchedule) as RoomScheduleParam,
      })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
      return true
    },

    async changeAvatar (image, roomId, roomPServedUser, executor, filePath) {
      try {
        let avatar
        if (filePath) {
          avatar = await usePromptServer().saveAvatar(image, filePath, `avatar${Date.now()}`)
        } else {
          avatar = await usePromptServer().saveAvatar(image, `avatar${roomId}`, `avatar${Date.now()}`)
        }

        const commandResponse = await im.changeConferenceAvatar({
          pServed: roomPServedUser,
          avatarPath: avatar,
        })
        storeRooms.updateRoom(roomId, { avatar })
        addMessage(roomPServedUser, commandResponse)
        autoMarkAsReadIfExecutor(commandResponse, roomPServedUser)
        return true
      } catch (error) {
        console.error(error)
      }

      return false
    },

    async removeAvatar (path: string, pServed: string, roomId: string): Promise<void> {
      try {
        await usePromptServer().removeFile(path)
        const commandResponse = await im.changeConferenceAvatar({
          pServed,
          avatarPath: null,
        })
        const room = storeRooms.rooms.get(roomId)
        if (room) {
          delete (room as unknown as Record<string, unknown>).avatar
        }
        addMessage(pServed, commandResponse)
        autoMarkAsReadIfExecutor(commandResponse, pServed)
      } catch (e) {
        console.error(e)
      }
    },

    async createConference (payload: CreateConferencePayload): Promise<ConferenceCreatedByYouEvent> {
      return im.createConference(payload).then((event: HistoryMessageItem<ConferenceCreatedByYouEvent>) => event.item)
    },

    async createConferenceFromDialog (chatName: string, replaceableCallId: string, userList: Array<string>): Promise<boolean> {
      await im.createConference({
        mode: ConferenceType.FREE_FOR_ALL,
        name: chatName,
        subscribers: userList.map((number) => ({ number, role: 'User' })),
        replaceableCallId,
      })
      return true
    },

    async addMember (pServed: string, targetInternalNumberRaw: Contact['internalNumber'], command: 'AddMember' | 'AddMemberAndInvite' = 'AddMember', temporary?: boolean, role?: ConferenceMemberRole) {
      const { addSubscriber } = useIM()
      const targetPServed = isNotPServed(targetInternalNumberRaw)
        ? numberToUri(targetInternalNumberRaw)
        : targetInternalNumberRaw
      const isBot = isBotPServed(targetPServed)
      const isSubscriber = await im.subscribeOnConferenceUpdates({
        pServed: targetPServed,
      })
      const targetInternalNumber = isBot
        ? fromPServedUser(targetPServed)
        : (isSubscriber ? targetInternalNumberRaw : `${PSTNGUEST}-${targetInternalNumberRaw}`)

      const commandResponse = await addSubscriber({
        pServed: pServed,
        targetInternalNumber,
        temporary: temporary!,
        needToInvite: command === 'AddMemberAndInvite',
      })
      if (role === 'Moderator') {
        try {
          await im.changeSubscriberRole({ pServed: pServed, targetNumber: targetInternalNumber, targetRole: 'Moderator' })
        } catch (e) {
          console.error(e)
        }
      }
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
      return true
    },
    async delete (targetEventId: HistoryMessageItem['eventId'], pServed: PServed): Promise<boolean> {
      const chatStore = chatModel.useChatMessageStore()
      const message = chatStore.findById(targetEventId)
      if (message?.status === MessageStatusType.ERROR || message?.status === MessageStatusType.INITIAL) {
        deleteUnsentMessage(targetEventId)
      } else {
        await im.deleteMessage({
          to: pServed,
          targetEventId: targetEventId,
        })
      }
      chatStore.resetMessage(targetEventId, pServed)
      closeVoicePlaybackIfCurrentEvent(targetEventId)
      useStoreLastReadEventIds().handleDeletedMessage(pServed, targetEventId)
      return true
    },
    async deleteForSelf (targetEventId: HistoryMessageItem['eventId'], pServed: PServed): Promise<boolean> {
      const chatStore = chatModel.useChatMessageStore()
      await im.deleteMessage({
        to: pServed,
        targetEventId: targetEventId,
      })
      chatStore.hideMessageForSelf(targetEventId, pServed)
      closeVoicePlaybackIfCurrentEvent(targetEventId)
      useStoreLastReadEventIds().handleDeletedMessage(pServed, targetEventId)
      return true
    },
    async setupPincode (pServed: string) {
      const commandResponse = await im.enableConferencePinCode({
        pServed,
      })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
    },
    async disablePincode (pServed: string) {
      const commandResponse = await im.disableConferencePinCode({ pServed })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
    },
    async changePincode (pServed: string) {
      const commandResponse = await im.renewConferencePinCode({ pServed })
      addMessage(pServed, commandResponse)
      autoMarkAsReadIfExecutor(commandResponse, pServed)
    },
    removeMember,
    leaveRoom,
    deleteRoom,
    refreshAccessToken,
    resend,
  }
}
